import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type ModelDistributionKeyStatus = 'active' | 'disabled'
export type ModelDistributionKeyStreamMode = 'allow' | 'deny'
export type QuotaMetric =
  | 'completion_tokens'
  | 'concurrency'
  | 'cost_amount'
  | 'prompt_tokens'
  | 'request_count'
  | 'total_tokens'
export type QuotaWindow = 'all_time' | 'day' | 'hour' | 'minute' | 'month'
export type QuotaScope = 'all_models' | 'per_public_model' | 'per_registered_model'
export type QuotaAction = 'block' | 'notify_only'

export interface ModelDistributionAllowedModel {
  id: string
  model_name: string
  provider: string
  provider_label?: string
}

export interface ModelDistributionKeyQuotaRule {
  action: QuotaAction
  created_at?: string
  currency?: string | null
  distribution_key_id?: string
  enabled?: boolean
  id?: string
  metric: QuotaMetric
  priority?: number
  scope: QuotaScope
  scope_value?: string | null
  threshold: number | string
  updated_at?: string
  window: QuotaWindow
}

export interface ModelDistributionKey {
  allowed_models: ModelDistributionAllowedModel[]
  created_at: string
  created_by?: string | null
  created_by_name?: string | null
  expires_at?: string | null
  id: string
  key_prefix: string
  masked_key?: string
  name: string
  output_protocol: string
  quota_rules?: ModelDistributionKeyQuotaRule[]
  status: ModelDistributionKeyStatus
  stream_mode?: ModelDistributionKeyStreamMode
  updated_at?: string
}

export interface ModelDistributionKeyDetail extends ModelDistributionKey {
  plain_key?: string
}

export interface ModelDistributionKeysListParams {
  page: number
  pageSize: number
  search?: string
  status?: ModelDistributionKeyStatus | ''
}

export interface ModelDistributionKeysListData {
  items: ModelDistributionKey[]
  page: number
  page_size: number
  total: number
}

export interface ModelDistributionKeyFormValues {
  allowedModelIds: string[]
  name: string
  outputProtocol: string
  quotaRules: ModelDistributionKeyQuotaRule[]
  status: ModelDistributionKeyStatus
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message?: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

function trimmedOrNull(value?: string | null) {
  const normalized = (value ?? '').trim()

  return normalized.length > 0 ? normalized : null
}

function quotaRulePayload(rule: ModelDistributionKeyQuotaRule) {
  return {
    action: rule.action,
    currency: trimmedOrNull(rule.currency),
    enabled: rule.enabled !== false,
    metric: rule.metric,
    priority: rule.priority ?? 0,
    scope: rule.scope,
    scope_value: trimmedOrNull(rule.scope_value),
    threshold: String(rule.threshold).trim(),
    window: rule.window,
  }
}

function distributionKeyPayload(values: ModelDistributionKeyFormValues) {
  return {
    allowed_model_ids: values.allowedModelIds.map((modelId) => modelId.trim()).filter(Boolean),
    name: values.name.trim(),
    output_protocol: values.outputProtocol,
    quota_rules: values.quotaRules.map(quotaRulePayload),
    status: values.status,
  }
}

export async function listModelDistributionKeys({
  page,
  pageSize,
  search,
  status,
}: ModelDistributionKeysListParams) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'search', search)
  appendOptionalQuery(params, 'status', status)

  const response = await apiClient.request<SuccessEnvelope<ModelDistributionKeysListData>>(
    `/admin/model-keys?${params.toString()}`,
  )

  return response.data
}

export async function getModelDistributionKeyDetail(keyId: string) {
  const response = await apiClient.request<SuccessEnvelope<ModelDistributionKeyDetail>>(
    `/admin/model-keys/${encodeURIComponent(keyId)}`,
  )

  return response.data
}

export async function createModelDistributionKey(values: ModelDistributionKeyFormValues) {
  const response = await apiClient.request<SuccessEnvelope<ModelDistributionKeyDetail>>(
    '/admin/model-keys',
    {
      body: JSON.stringify(distributionKeyPayload(values)),
      method: 'POST',
    },
  )

  return response.data
}

export async function updateModelDistributionKey(
  keyId: string,
  values: ModelDistributionKeyFormValues,
) {
  const response = await apiClient.request<SuccessEnvelope<ModelDistributionKeyDetail>>(
    `/admin/model-keys/${encodeURIComponent(keyId)}`,
    {
      body: JSON.stringify(distributionKeyPayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function rotateModelDistributionKey(keyId: string) {
  const response = await apiClient.request<SuccessEnvelope<ModelDistributionKeyDetail>>(
    `/admin/model-keys/${encodeURIComponent(keyId)}/rotate`,
    {
      method: 'POST',
    },
  )

  return response.data
}

export async function deleteModelDistributionKey(keyId: string) {
  return apiClient.request<MessageEnvelope>(`/admin/model-keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE',
  })
}
